import { DragEvent, useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { enqueueSnackbar } from "notistack"
import { useNavigate } from "react-router-dom"
import { UseQueryResult } from "@tanstack/react-query"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { Box, FormControlLabel, Grid, Switch, Typography } from "@mui/material"
import { MRT_ColumnDef, MRT_FullScreenToggleButton, MRT_Row } from "material-react-table"
import { MerchantProductsQuery, MoveProductBody, fetchProducts, moveProduct } from "../productsApi"
import { renderRowActionMenuItems } from "../components/tableElements/renderRowActionMenuItems"
import { MoveProductGroupBody, moveProductGroup } from "../../productGroup/productGroupApi"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { SelectShop } from "../../shops/components/SelectShop"
import {
    DefaultConfirmModalStateType,
    DeleteProductDialog,
    defaultConfirmModalState,
} from "../components/tableElements/DeleteProductDialog"
import {
    DefaultPreviewProductImageStateType,
    PreviewProductImage,
    defaultPreviewProductImageState,
} from "../components/tableElements/PreviewProductImage"
import { ProductTableRow } from "../productsTypes"
import { transformProductsData } from "../helpers"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ProductGroupItemBox, ProductName } from "../ProductTableColumns"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"

const useStyles = makeStyles()((theme) => ({
    topToolbarCustomWrap: {
        margin: "0.5rem 1rem",
        gap: "1rem",
        width: "auto",
    },

    hideRowActions: {
        "& td:last-child .MuiIconButton-root": {
            display: "none",
        },
    },
    infoWrap: {
        display: "grid",
        alignItems: "center",
        gridTemplateColumns: "1rem 1fr",
        background: theme.palette.tailwind.sky[200],
        borderRadius: "0.5rem",
        gap: "0.625rem",
        padding: "0.75rem",
        color: theme.palette.tailwind.sky[800],
        width: "100%",
        marginBottom: "1.5rem",
        "&> svg": {
            display: "block",
        },
    },
    deleteItem: {
        color: theme.palette.tailwind.red[600],
    },
}))

function getNewItemPosition(
    shopId: string | undefined,
    draggingRow: MRT_Row<ProductTableRow>,
    targetRow: MRT_Row<ProductTableRow>,
): MoveProductBody | MoveProductGroupBody | null {
    const draggingItem = draggingRow.original
    const targetItem = targetRow.original

    if (!shopId) return null

    //TODO: TBD: if moving products between groups will be allowed, this logic should be updated
    // move product on place after a target product inside the same group
    if (
        draggingItem.type === "product" &&
        targetItem.type === "product" &&
        draggingItem.groupId &&
        draggingItem.groupId === targetItem.groupId
    ) {
        return {
            shop: {
                id: shopId,
            },
            moveTo: {
                position: Number(targetRow.id),
            },
        }
    }

    // move product in another group
    if (
        draggingItem.type === "product" &&
        targetItem.type === "product" &&
        draggingItem.groupId &&
        draggingItem.groupId !== targetItem.groupId
    ) {
        return {
            shop: {
                id: shopId,
            },
            moveTo: {
                productGroup: {
                    id: targetItem.id,
                },
                position: Number(targetRow.id),
            },
        }
    }

    // put at the first place in the group
    if (draggingItem.type === "product" && targetItem.type === "group" && draggingItem.groupId) {
        return {
            shop: {
                id: shopId,
            },
            moveTo: {
                ...(draggingItem.groupId !== targetItem.id && {
                    productGroup: {
                        id: targetItem.id,
                    },
                }),
                position: Number(targetRow.id) + 1,
            },
        }
    }

    // move group after a target group
    if (draggingItem.type === "group" && targetItem.type === "group") {
        return {
            shop: {
                id: shopId,
            },
            moveTo: {
                position: Number(targetRow.id),
            },
        }
    }

    return null
}

type ProductGroupPageProps = Readonly<{}>

export function SortProductsTab({}: ProductGroupPageProps) {
    const { merchantId } = useMerchant()
    const { classes } = useStyles()
    const navigate = useNavigate()

    const [confirmModalState, setConfirmModalState] = useState<DefaultConfirmModalStateType>(defaultConfirmModalState)
    const [previewImage, setPreviewImage] = useState<DefaultPreviewProductImageStateType>(
        defaultPreviewProductImageState,
    )
    const [shopId, setShopId] = useState<string>()
    const [onlyInactive, setOnlyInactive] = useState<boolean>(false)

    const { l10n } = useAppLocalization()

    const products = useApiQuery(fetchProducts, {
        queryName: MerchantProductsQuery.LIST,
        dependencies: {
            merchantId: merchantId,
            shopId,
        },
        enabled: Boolean(shopId),
        select: transformProductsData,
    })

    const tableData = useMemo(() => {
        if (!Array.isArray(products.data)) {
            return []
        }

        if (onlyInactive) {
            return products.data.filter((product) => !product.isActive)
        }

        return products.data
    }, [products.data, onlyInactive])

    const { ApiQueryTable, columnFactory } = useApiQueryTable({
        ...products,
        data: tableData,
    } as UseQueryResult<typeof tableData>)

    const columns = useMemo<MRT_ColumnDef<ProductTableRow>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("products-name-column"),
                accessorKey: "name",
                Cell: ({ row }) => {
                    if (row.original.type === "group") {
                        return <ProductGroupItemBox productGroup={row.original} />
                    }

                    if (row.original.type === "product") {
                        return (
                            <ProductName
                                product={row.original}
                                onProductPhotoClick={(imageUrl) => {
                                    setPreviewImage({ show: true, image: imageUrl })
                                }}
                            />
                        )
                    }

                    return ""
                },
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("products-price-column"),
                    id: "price",
                },
                (row) => (row.type === "group" ? undefined : row.price),
                true,
            ),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("products-status-column"),
                    id: "status",
                },
                (row) => row.isActive ?? false,
                {
                    cellOverride: (row) => (row.type === "group" ? "" : null),
                },
            ),
        ],
        [l10n, columnFactory, setPreviewImage],
    )

    const { mutateAsync: callMoveProduct } = useApiMutation(moveProduct, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MerchantProductsQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const { mutateAsync: callMoveProductGroup } = useApiMutation(moveProductGroup, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MerchantProductsQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    return (
        <Grid container direction="column">
            {!shopId && (
                <Box className={classes.infoWrap}>
                    <FontAwesomeIcon icon={faCircleInfo} />
                    <LocalizedStrict id="products-table-info-box">
                        <Typography variant="subtitle1">Please select a shop to proceed</Typography>
                    </LocalizedStrict>
                </Box>
            )}
            <ApiQueryTable
                columns={columns}
                enableStickyHeader
                enableDensityToggle={false}
                enableHiding={false}
                initialState={{ density: "spacious" }}
                enableRowActions
                positionActionsColumn="last"
                enableRowOrdering
                renderTopToolbarCustomActions={() => (
                    <Grid container className={classes.topToolbarCustomWrap}>
                        <SelectShop shopId={shopId} setShopId={setShopId} />
                    </Grid>
                )}
                muiTableBodyRowDragHandleProps={({ table }) => ({
                    onDragStart: (event) => {
                        // const acceptableRows = table.getState().rows.filter((row) => row.original.type === "product")
                    },
                    onDragEnd: (event: DragEvent<HTMLButtonElement>) => {
                        const { draggingRow, hoveredRow } = table.getState()
                        //data re-ordering logic here
                        if (hoveredRow && draggingRow) {
                            const draggingItem = draggingRow.original
                            const position = getNewItemPosition(
                                shopId,
                                draggingRow,
                                hoveredRow as MRT_Row<ProductTableRow>,
                            )

                            if (position !== null && draggingItem.type === "product") {
                                callMoveProduct({
                                    productId: draggingItem.id,
                                    position,
                                })
                            }

                            if (position !== null && draggingItem.type === "group") {
                                callMoveProductGroup({
                                    productGroupId: draggingItem.id,
                                    position,
                                })
                            }
                        }
                    },
                })}
                enableColumnActions={false}
                renderRowActionMenuItems={({ row, closeMenu }) =>
                    renderRowActionMenuItems({
                        row,
                        closeMenu,
                        setConfirmModalState,
                        navigate,
                        errorClasses: classes.deleteItem,
                    })
                }
                muiTableBodyRowProps={({ row }) => ({
                    className: row.original.type === "group" ? classes.hideRowActions : "",
                })}
                renderToolbarInternalActions={({ table }) => (
                    <Grid container wrap="nowrap" flex="none">
                        <LocalizedStrict id="products-table-show-inactive" attrs={{ label: true }}>
                            <FormControlLabel
                                control={
                                    <Switch checked={onlyInactive} onClick={() => setOnlyInactive((prev) => !prev)} />
                                }
                                label="Show inactive"
                            />
                        </LocalizedStrict>
                        <MRT_FullScreenToggleButton table={table} />
                    </Grid>
                )}
            />

            {/* Confirm delete */}
            {confirmModalState.showConfirmModal && (
                <DeleteProductDialog
                    confirmModalState={confirmModalState}
                    setConfirmModalState={setConfirmModalState}
                />
            )}

            {/* Product preview */}
            {previewImage.show && <PreviewProductImage previewImage={previewImage} setPreviewImage={setPreviewImage} />}
        </Grid>
    )
}
